export const EVENT_SHORT_DESCRIPTION_MAX_LENGTH = 300
export const EVENT_FULL_DESCRIPTION_MAX_LENGTH = 3_000
export const EVENT_RULES_MAX_LENGTH = 5_000
export const EVENT_RICH_TEXT_SOURCE_MAX_LENGTH = 256_000

export type EventPaymentMethod = 'online' | 'cash'

const EVENT_PAYMENT_METHOD_DISPLAY_ORDER: readonly EventPaymentMethod[] = ['online', 'cash']

export function formatEventPaymentMethods(
    methods: readonly string[] | null | undefined,
    labels: Record<EventPaymentMethod, string>,
    priceMinor: number | null | undefined
): string | null {
    if (typeof priceMinor !== 'number' || priceMinor <= 0 || !methods?.length) return null
    const available = new Set(methods)
    const value = EVENT_PAYMENT_METHOD_DISPLAY_ORDER.filter((method) => available.has(method))
        .map((method) => labels[method])
        .join(', ')
    return value || null
}

export type EventContentLengthViolations = {
    description: boolean
    eventRules: boolean
    fullDescription: boolean
}

export type EventContent = {
    description?: string | null
    eventRules?: string | null
    fullDescription?: string | null
}

const normalizeContent = (value: string | null | undefined) => value?.trim() ?? ''

export function countRichTextCharacters(value: string): number {
    if (typeof document !== 'undefined') {
        const container = document.createElement('div')
        container.innerHTML = value
        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT)
        let length = 0
        let node = walker.nextNode()
        while (node) {
            if (node.nodeType === Node.TEXT_NODE) length += node.textContent?.length ?? 0
            if (node instanceof HTMLImageElement || node instanceof HTMLBRElement || node instanceof HTMLHRElement)
                length += 1
            node = walker.nextNode()
        }
        return length
    }

    return value
        .replace(/<(?:img|br|hr)\b[^>]*>/giu, '\ufffc')
        .replace(/<[^>]*>/gu, '')
        .replace(/&#(\d+);/gu, (_, code: string) => String.fromCodePoint(Number(code)))
        .replace(/&#x([\da-f]+);/giu, (_, code: string) => String.fromCodePoint(Number.parseInt(code, 16)))
        .replace(/&nbsp;/giu, '\u00a0')
        .replace(/&amp;/giu, '&')
        .replace(/&lt;/giu, '<')
        .replace(/&gt;/giu, '>')
        .replace(/&quot;/giu, '"')
        .replace(/&#39;/giu, "'").length
}

export function getEventContentLengthViolations(
    content: EventContent,
    baseline?: EventContent
): EventContentLengthViolations {
    const description = normalizeContent(content.description)
    const fullDescription = normalizeContent(content.fullDescription)
    const eventRules = normalizeContent(content.eventRules)
    return {
        description:
            description.length > EVENT_SHORT_DESCRIPTION_MAX_LENGTH &&
            (!baseline || description !== normalizeContent(baseline.description)),
        fullDescription:
            countRichTextCharacters(fullDescription) > EVENT_FULL_DESCRIPTION_MAX_LENGTH &&
            (!baseline || fullDescription !== normalizeContent(baseline.fullDescription)),
        eventRules:
            countRichTextCharacters(eventRules) > EVENT_RULES_MAX_LENGTH &&
            (!baseline || eventRules !== normalizeContent(baseline.eventRules)),
    }
}

const PUBLIC_EVENT_CODE_LENGTH = 5
const PUBLIC_EVENT_CODE_SOURCE = '(?:[23456789abcdefghjkmnpqrstuvwxyz]{5}|1[0-9a-f]{4})'
export const PUBLIC_EVENT_CODE_PATTERN = new RegExp(`^${PUBLIC_EVENT_CODE_SOURCE}$`, 'u')

const PUBLIC_EVENT_TITLE_SLUG_MAX_LENGTH = 60
const PUBLIC_EVENT_REFERENCE_MAX_LENGTH = PUBLIC_EVENT_TITLE_SLUG_MAX_LENGTH + 1 + PUBLIC_EVENT_CODE_LENGTH
const PUBLIC_EVENT_REFERENCE_PATTERN = new RegExp(`^[a-z0-9]+(?:-[a-z0-9]+)*-(${PUBLIC_EVENT_CODE_SOURCE})$`, 'u')

type PublicEventReferenceInput = {
    className?: string | null
    publicCode?: string | null
}

function createPublicEventTitleSlug(title: string | null | undefined): string {
    const normalized = (title ?? '')
        .trim()
        .toLocaleLowerCase('pl-PL')
        .replaceAll('ł', 'l')
        .normalize('NFKD')
        .replace(/\p{M}+/gu, '')
        .replace(/[^a-z0-9]+/gu, '-')
        .replace(/^-+|-+$/gu, '')

    return normalized.slice(0, PUBLIC_EVENT_TITLE_SLUG_MAX_LENGTH).replace(/-+$/gu, '') || 'wydarzenie'
}

function getPublicEventReference(event: PublicEventReferenceInput): string | null {
    const publicCode = event.publicCode?.trim()
    if (!publicCode || !PUBLIC_EVENT_CODE_PATTERN.test(publicCode)) return null
    return `${createPublicEventTitleSlug(event.className)}-${publicCode}`
}

export function getPublicEventCodeFromReference(reference: string): string | null {
    const normalizedReference = reference.trim()
    if (normalizedReference.length > PUBLIC_EVENT_REFERENCE_MAX_LENGTH) return null
    const match = PUBLIC_EVENT_REFERENCE_PATTERN.exec(normalizedReference)
    return match?.[1] ?? null
}

/** Public sections by activity: an Occurrence of a recurring class, a one-off event and a course each have their own. */
export const PUBLIC_ACTIVITY_SECTIONS = {class: 'zajecia', event: 'wydarzenia', course: 'kursy'} as const
export type PublicActivityKind = keyof typeof PUBLIC_ACTIVITY_SECTIONS

export function getPublicActivityPath(kind: PublicActivityKind, event: PublicEventReferenceInput): string | null {
    const reference = getPublicEventReference(event)
    return reference ? `/${PUBLIC_ACTIVITY_SECTIONS[kind]}/${encodeURIComponent(reference)}` : null
}

/**
 * The public page of a Term: `/zajecia/…` for a recurring class, `/wydarzenia/…` for a one-off event.
 * The kind is required: a one-off event's link, already published by studios, never changes.
 */
export function getPublicEventPath(event: PublicEventReferenceInput & {isCyclical: boolean}): string | null {
    return getPublicActivityPath(event.isCyclical ? 'class' : 'event', event)
}

type PublicEventShareFingerprintInput = {
    classCoverImageUrl?: string | null
    classColorKey?: string | null
    className?: string | null
    startDateTime?: string | null
    timeZone?: string | null
    studioName?: string | null
    locationName?: string | null
}

const PUBLIC_EVENT_SOCIAL_IMAGE_VERSION = '7'

function fnv1a32(value: string, seed: number): number {
    let hash = seed >>> 0
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index)
        hash = Math.imul(hash, 0x01000193) >>> 0
    }
    return hash
}

/**
 * Cache identity for a share preview. It is intentionally not a security
 * primitive; changing any user-visible preview input produces a new immutable URL.
 */
export function getShareImageFingerprint(values: ReadonlyArray<string | null | undefined>): string {
    const source = values.map((value) => value?.trim() ?? '').join('\u001f')
    const first = fnv1a32(source, 0x811c9dc5).toString(16).padStart(8, '0')
    const second = fnv1a32(source, 0x9e3779b9).toString(16).padStart(8, '0')
    return `${first}${second}`
}

export function getPublicEventShareFingerprint(event: PublicEventShareFingerprintInput): string {
    return getShareImageFingerprint([
        PUBLIC_EVENT_SOCIAL_IMAGE_VERSION,
        event.className,
        event.startDateTime,
        event.timeZone,
        event.classCoverImageUrl,
        event.classColorKey,
        event.studioName,
        event.locationName,
    ])
}

export function getPublicEventShareTitle(
    eventName: string | null | undefined,
    studioName: string | null | undefined
): string {
    const normalizedEventName = eventName?.trim() || 'Wydarzenie'
    const normalizedStudioName = studioName?.trim()
    return normalizedStudioName ? `${normalizedEventName} – ${normalizedStudioName}` : normalizedEventName
}

export function getPublicEventShareDescription(
    description: string | null | undefined,
    fallback = 'Sprawdź szczegóły wydarzenia i dostępność miejsc.'
): string {
    const normalized = description?.replace(/\s+/gu, ' ').trim()
    if (!normalized) return fallback
    return normalized.length <= 200 ? normalized : `${normalized.slice(0, 197).trimEnd()}…`
}

export function truncatePublicEventShareImageText(value: string | null | undefined, maxGraphemes: number): string {
    const normalized = value?.replace(/\s+/gu, ' ').trim() ?? ''
    if (!normalized || !Number.isSafeInteger(maxGraphemes) || maxGraphemes < 2) return normalized

    const graphemes = Array.from(
        new Intl.Segmenter('pl', { granularity: 'grapheme' }).segment(normalized),
        (segment) => segment.segment
    )
    if (graphemes.length <= maxGraphemes) return normalized
    return `${graphemes
        .slice(0, maxGraphemes - 1)
        .join('')
        .trimEnd()}…`
}
